// Settings redesign (owner, 2026-10-22): the API calls behind the Settings pages that had no client
// yet — checkout options (CHECKOUT-001), language and region (SETTINGS-001), digital products
// (PRODUCT-001), loyalty (LOYALTY-001), order-protection lists (RISK-001), tax classes and invoice
// details, the help centre and tickets (SUPPORT-001) and the organization (ORG-001). Shapes mirror
// the server packages (packages/domain/{checkout,store,product,loyalty,risk,system,tenancy}).
import type { QueryClient } from "@tanstack/react-query";
import type { ShopIdea, ShopWhatsNew } from "@ady/contracts";
import { request } from "../api/client";

const o_ = (o: string) => `/orgs/${encodeURIComponent(o)}`;
const s = (o: string, st: string) => `${o_(o)}/stores/${encodeURIComponent(st)}`;
const id = encodeURIComponent;

// ------------------------------------------------------------------ checkout options

export type FieldRule = "hidden" | "optional" | "required";
export interface CustomField {
  id: string;
  label: string;
  type: "text" | "textarea" | "select" | "checkbox" | "date";
  required: boolean;
  options: string[];
  help: string;
}
export interface CheckoutSettings {
  min_order_paisa: number;
  cod_fee_paisa: number;
  email: FieldRule;
  phone_alt: boolean;
  order_note: boolean;
  gift_message: boolean;
  b2b_invoice: boolean;
  preferred_date: boolean;
  whatsapp_number: string | null;
  sla_hours: number;
  promo_field: boolean;
  guest_checkout: boolean;
  terms: { enabled: boolean; text: string };
  landmark: FieldRule;
  custom_fields: CustomField[];
  thank_you_note: string;
  version: number;
}

// ------------------------------------------------------------------ region

export interface RegionSettings {
  bs_dates_enabled: boolean;
  locale: string;
  timezone: string;
  currency: "NPR";
  version: number;
}

// ------------------------------------------------------------------ taxes and invoices

export interface InvoiceSettings {
  footer_note: string;
  terms: string;
  show_pan: boolean;
  show_logo: boolean;
  signature_line: boolean;
  show_qr: boolean;
}
export interface TaxSettingsFull {
  vat_registered: boolean;
  prices_include_vat: boolean;
  delivery_taxable: boolean;
  vat_number: string | null;
  rates: { standard: number; exempt: number; zero: number };
  invoice: InvoiceSettings;
  version: number;
}
export interface TaxClass {
  key: "standard" | "exempt" | "zero";
  rate_bp: number;
  label: string;
  custom: boolean;
}

// ------------------------------------------------------------------ order protection lists

export interface RiskListEntry {
  list: "block" | "allow";
  kind: "phone" | "email";
  value: string;
  reason: string | null;
  created_at: string;
}

// ------------------------------------------------------------------ digital products

export interface DigitalSettings {
  enabled: boolean;
  download_limit: number;
  link_expiry_days: number;
  email_links: boolean;
  show_on_thanks_page: boolean;
  delivery_note: string;
}
export type DigitalKind = "file" | "key" | "link";
export interface DigitalFile {
  file_id: string;
  name: string;
  size_bytes: number;
}
export interface DigitalItem {
  product_id: string;
  kind: DigitalKind;
  files: DigitalFile[];
  link_url: string | null;
  instructions: string;
  download_limit: number | null;
  updated_at: string;
}
export interface DigitalProductRow {
  product_id: string;
  title: string;
  image_asset_id: string | null;
  kind: DigitalKind;
  files_count: number;
  keys_available: number;
  keys_delivered: number;
  deliveries: number;
  updated_at: string;
}
export interface DigitalKey {
  id: string;
  product_id: string;
  variant_id: string | null;
  masked: string;
  state: "available" | "delivered" | "revoked";
  order_number: string | null;
  delivered_at: string | null;
  created_at: string;
}
export interface DigitalDelivery {
  id: string;
  order_id: string;
  order_number: string;
  customer_name: string | null;
  product_title: string;
  kind: DigitalKind;
  downloads_used: number;
  download_limit: number;
  expires_at: string | null;
  state: "active" | "expired" | "revoked" | "waiting_key";
  created_at: string;
}

// ------------------------------------------------------------------ loyalty

export interface LoyaltyTier {
  id: string;
  name: string;
  min_spend_paisa: number;
  multiplier_bp: number;
  perk: string;
}
export type RewardKind = "amount_off" | "percent_off" | "free_delivery" | "gift";
export interface LoyaltyReward {
  id: string;
  name: string;
  points: number;
  kind: RewardKind;
  amount_paisa?: number;
  percent_bp?: number;
  product_id?: string;
  tier_id?: string | null;
  active: boolean;
}
export interface LoyaltyProgram {
  active: boolean;
  name: string;
  earn: { points_per_100: number; include_delivery: boolean };
  redeem: { point_value_paisa: number; min_points: number; max_percent: number };
  expiry_days: number | null;
  welcome_points: number;
  tiers: LoyaltyTier[];
  rewards: LoyaltyReward[];
}
export interface LoyaltySummary {
  members: number;
  active_30d: number;
  points_outstanding: number;
  points_earned_30d: number;
  points_redeemed_30d: number;
  liability_paisa: number;
  by_tier: { tier_id: string | null; members: number }[];
}
export interface LoyaltyMember {
  customer_id: string;
  name: string | null;
  phone: string | null;
  member_no: string;
  tier_id: string | null;
  points: number;
  lifetime_spend_paisa: number;
  last_activity_at: string | null;
  joined_at: string;
}
export interface PointsLedgerRow {
  id: string;
  kind: "earn" | "redeem" | "reverse" | "expire" | "adjust" | "welcome";
  points: number;
  order_id: string | null;
  order_number: string | null;
  note: string | null;
  created_at: string;
}
export interface CustomerPoints {
  customer_id: string;
  member_no: string | null;
  tier_id: string | null;
  points: number;
  lifetime_spend_paisa: number;
  ledger: PointsLedgerRow[];
}

// ------------------------------------------------------------------ help and organization

export interface HelpArticle {
  id: string;
  slug: string;
  title: string;
  body?: string;
  module: string;
  video_url: string | null;
  published: boolean;
  updated_at: string | null;
}
export type TicketCategory = "question" | "problem" | "billing" | "feature";
export interface Ticket {
  id: string;
  category: TicketCategory;
  subject: string;
  state: "open" | "answered" | "closed";
  priority: "low" | "normal" | "high";
  sla_due_at: string;
  created_at: string;
  updated_at: string;
}
export interface TicketMessage {
  id: string;
  // The server writes "vendor" for the shop's own messages (control 0573); older mocks wrote "merchant".
  author_type: "vendor" | "merchant" | "staff" | "system";
  body: string;
  created_at: string;
}
export interface Org {
  id: string;
  name: string;
  slug: string;
  lifecycle: string;
  owner_user_id: string;
  test_mode_enabled: boolean;
  referral_code: string | null;
  created_at: string;
  updated_at: string;
  version: number;
}

export const setKeys = {
  checkout: (o: string, st: string) => ["settings", o, st, "checkout"] as const,
  region: (o: string, st: string) => ["settings", o, st, "region"] as const,
  tax: (o: string, st: string) => ["settings", o, st, "tax"] as const,
  taxClasses: (o: string, st: string) => ["settings", o, st, "tax-classes"] as const,
  riskLists: (o: string, st: string) => ["settings", o, st, "risk-lists"] as const,
  digital: (o: string, st: string) => ["settings", o, st, "digital"] as const,
  digitalProducts: (o: string, st: string) => ["settings", o, st, "digital-products"] as const,
  digitalItem: (o: string, st: string, p: string) =>
    ["settings", o, st, "digital-item", p] as const,
  digitalKeys: (o: string, st: string, p: string) =>
    ["settings", o, st, "digital-keys", p] as const,
  deliveries: (o: string, st: string) => ["settings", o, st, "deliveries"] as const,
  loyalty: (o: string, st: string) => ["settings", o, st, "loyalty"] as const,
  loyaltySummary: (o: string, st: string) => ["settings", o, st, "loyalty-summary"] as const,
  members: (o: string, st: string, q: string) => ["settings", o, st, "members", q] as const,
  points: (o: string, st: string, c: string) => ["settings", o, st, "points", c] as const,
  help: () => ["settings", "help"] as const,
  article: (slug: string) => ["settings", "help", slug] as const,
  tickets: (o: string) => ["settings", o, "tickets"] as const,
  ticket: (o: string, t: string) => ["settings", o, "ticket", t] as const,
  ideas: (o: string) => ["settings", o, "ideas"] as const,
  whatsNew: (o: string) => ["settings", o, "whats-new"] as const,
  org: (o: string) => ["settings", o, "org"] as const,
};

export const setApi = {
  checkout: (o: string, st: string) =>
    request<CheckoutSettings>("GET", `${s(o, st)}/settings/checkout`),
  saveCheckout: (
    o: string,
    st: string,
    version: number,
    body: Partial<Omit<CheckoutSettings, "version">>,
  ) =>
    request<CheckoutSettings>("PATCH", `${s(o, st)}/settings/checkout`, { body, ifMatch: version }),

  region: (o: string, st: string) => request<RegionSettings>("GET", `${s(o, st)}/settings/region`),
  saveRegion: (
    o: string,
    st: string,
    version: number,
    body: Partial<Pick<RegionSettings, "bs_dates_enabled">>,
  ) => request<RegionSettings>("PATCH", `${s(o, st)}/settings/region`, { body, ifMatch: version }),

  tax: (o: string, st: string) => request<TaxSettingsFull>("GET", `${s(o, st)}/settings/tax`),
  saveTax: (
    o: string,
    st: string,
    version: number,
    body: Partial<Omit<TaxSettingsFull, "rates" | "version" | "invoice">> & {
      invoice?: Partial<InvoiceSettings>;
    },
  ) => request<TaxSettingsFull>("PATCH", `${s(o, st)}/settings/tax`, { body, ifMatch: version }),
  taxClasses: (o: string, st: string) =>
    request<{ data: TaxClass[] }>("GET", `${s(o, st)}/tax-classes`),
  saveTaxClass: (
    o: string,
    st: string,
    key: TaxClass["key"],
    body: { rate_bp: number; label: string },
  ) => request<TaxClass>("PUT", `${s(o, st)}/tax-classes/${id(key)}`, { body }),

  riskLists: (o: string, st: string) =>
    request<{ data: RiskListEntry[] }>("GET", `${s(o, st)}/risk/lists`),
  addRiskList: (
    o: string,
    st: string,
    body: {
      list: RiskListEntry["list"];
      kind: RiskListEntry["kind"];
      value: string;
      reason?: string;
    },
  ) => request<RiskListEntry>("POST", `${s(o, st)}/risk/lists`, { body }),
  removeRiskList: (
    o: string,
    st: string,
    body: { list: RiskListEntry["list"]; kind: RiskListEntry["kind"]; value: string },
  ) => request<void>("POST", `${s(o, st)}/risk/lists/remove`, { body }),

  digital: (o: string, st: string) =>
    request<{ settings: DigitalSettings; version: number }>("GET", `${s(o, st)}/settings/digital`),
  saveDigital: (o: string, st: string, version: number, body: DigitalSettings) =>
    request<{ settings: DigitalSettings; version: number }>("PUT", `${s(o, st)}/settings/digital`, {
      body,
      ifMatch: version,
    }),
  digitalProducts: (o: string, st: string) =>
    request<{ items: DigitalProductRow[] }>("GET", `${s(o, st)}/digital-products`),
  digitalItem: (o: string, st: string, p: string) =>
    request<DigitalItem>("GET", `${s(o, st)}/products/${id(p)}/digital`),
  putDigitalItem: (
    o: string,
    st: string,
    p: string,
    body: Omit<DigitalItem, "product_id" | "updated_at">,
  ) => request<DigitalItem>("PUT", `${s(o, st)}/products/${id(p)}/digital`, { body }),
  removeDigitalItem: (o: string, st: string, p: string) =>
    request<void>("DELETE", `${s(o, st)}/products/${id(p)}/digital`),
  uploadDigitalFile: (o: string, st: string, file: File) =>
    request<DigitalFile>("POST", `${s(o, st)}/digital-files`, {
      raw: file,
      headers: {
        "content-type": file.type || "application/octet-stream",
        "x-file-name": encodeURIComponent(file.name),
      },
    }),
  digitalKeys: (o: string, st: string, p: string) =>
    request<{ items: DigitalKey[]; counts: { available: number; delivered: number } }>(
      "GET",
      `${s(o, st)}/digital-keys`,
      {
        query: { product_id: p },
      },
    ),
  addDigitalKeys: (
    o: string,
    st: string,
    body: { product_id: string; variant_id?: string | null; keys: string[] },
    key: string,
  ) =>
    request<{ added: number; duplicates: number }>("POST", `${s(o, st)}/digital-keys`, {
      body,
      idempotencyKey: key,
    }),
  revealKey: (o: string, st: string, k: string) =>
    request<{ key: string }>("POST", `${s(o, st)}/digital-keys/${id(k)}/reveal`, { body: {} }),
  removeKey: (o: string, st: string, k: string) =>
    request<void>("DELETE", `${s(o, st)}/digital-keys/${id(k)}`),
  deliveries: (o: string, st: string) =>
    request<{ items: DigitalDelivery[]; next_cursor: string | null }>(
      "GET",
      `${s(o, st)}/digital-deliveries`,
    ),
  deliveryAction: (o: string, st: string, d: string, action: "resend" | "reset" | "revoke") =>
    request<DigitalDelivery>("POST", `${s(o, st)}/digital-deliveries/${id(d)}/${action}`, {
      body: {},
    }),

  loyalty: (o: string, st: string) =>
    request<{ program: LoyaltyProgram; version: number; updated_at: string | null }>(
      "GET",
      `${s(o, st)}/loyalty/program`,
    ),
  saveLoyalty: (o: string, st: string, version: number, body: LoyaltyProgram) =>
    request<{ program: LoyaltyProgram; version: number; updated_at: string | null }>(
      "PUT",
      `${s(o, st)}/loyalty/program`,
      {
        body,
        ifMatch: version,
      },
    ),
  loyaltySummary: (o: string, st: string) =>
    request<LoyaltySummary>("GET", `${s(o, st)}/loyalty/summary`),
  members: (o: string, st: string, q: string) =>
    request<{ items: LoyaltyMember[]; next_cursor: string | null }>(
      "GET",
      `${s(o, st)}/loyalty/members`,
      {
        query: { q: q || undefined, limit: 50 },
      },
    ),
  points: (o: string, st: string, c: string) =>
    request<CustomerPoints>("GET", `${s(o, st)}/customers/${id(c)}/points`),
  adjustPoints: (
    o: string,
    st: string,
    c: string,
    body: { points: number; note: string },
    key: string,
  ) =>
    request<CustomerPoints>("POST", `${s(o, st)}/customers/${id(c)}/points/adjust`, {
      body,
      idempotencyKey: key,
    }),

  help: () => request<{ data: HelpArticle[] }>("GET", "/help"),
  article: (slug: string) => request<{ article: HelpArticle }>("GET", `/help/${id(slug)}`),
  tickets: (o: string) => request<{ data: Ticket[] }>("GET", `${o_(o)}/support/tickets`),
  ticket: (o: string, t: string) =>
    request<{ ticket: Ticket; messages: TicketMessage[] }>(
      "GET",
      `${o_(o)}/support/tickets/${id(t)}`,
    ),
  openTicket: (
    o: string,
    body: {
      category: TicketCategory;
      subject: string;
      body: string;
      store_id?: string;
      context?: Record<string, string>;
    },
  ) =>
    request<{ ticket: Ticket; messages: TicketMessage[] }>("POST", `${o_(o)}/support/tickets`, {
      body,
    }),
  replyTicket: (o: string, t: string, body: string) =>
    request<{ ticket: Ticket; messages: TicketMessage[] }>(
      "POST",
      `${o_(o)}/support/tickets/${id(t)}/messages`,
      {
        body: { body },
      },
    ),

  // D-067: ideas (one vote per shop) and What's new.
  ideas: (o: string) => request<{ data: ShopIdea[] }>("GET", `${o_(o)}/ideas`),
  createIdea: (o: string, body: { title: string; body: string }) =>
    request<{ data: ShopIdea[] }>("POST", `${o_(o)}/ideas`, { body }),
  voteIdea: (o: string, ideaId: string, on: boolean) =>
    request<{ data: ShopIdea[] }>("POST", `${o_(o)}/ideas/${id(ideaId)}/vote`, { body: { on } }),
  whatsNew: (o: string) => request<ShopWhatsNew>("GET", `${o_(o)}/whats-new`),
  seenWhatsNew: (o: string) => request<{ ok: true }>("POST", `${o_(o)}/whats-new/seen`),

  org: (o: string) => request<Org>("GET", o_(o)),
  renameOrg: (o: string, version: number, name: string) =>
    request<Org>("PATCH", o_(o), { body: { name }, ifMatch: version }),
  closeOrg: (o: string, reason: string) =>
    request<Org>("POST", `${o_(o)}/close`, { body: { reason } }),
};

/**
 * Store profile, region, checkout and tax settings share one row version on the server
 * (store_settings.version): after saving one, the others must be refetched before their next save.
 */
export function refreshSharedSettings(
  qc: QueryClient,
  o: string,
  st: string,
  except?: readonly unknown[],
): void {
  for (const key of [
    setKeys.checkout(o, st),
    setKeys.region(o, st),
    setKeys.tax(o, st),
    ["sf", o, st, "settings"],
  ])
    if (JSON.stringify(key) !== JSON.stringify(except))
      void qc.invalidateQueries({ queryKey: key });
}
